import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Star, Clock, MapPin } from "lucide-react";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API = `${getBackendUrl()}/api`;

const FavouriteRestaurantsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [favouriteRestaurants, setFavouriteRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const favResponse = await axios.get(`${API}/favorites/${user.id}`);
      
      // Backend returns {restaurants: [...], dishes: [...]}
      const restaurants = favResponse.data.restaurants || [];
      setFavouriteRestaurants(restaurants);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (restaurantId) => {
    try {
      await axios.delete(`${API}/favorites/${user.id}/restaurant/${restaurantId}`);
      setFavouriteRestaurants(prev => prev.filter(r => r.id !== restaurantId));
    } catch (error) {
      console.error("Error removing favorite:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="favourite-restaurants-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate("/home")}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Favourite Restaurants</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {favouriteRestaurants.length === 0 ? (
          <div className="text-center py-12">
            <Star size={64} className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No favourite restaurants yet</h2>
            <p className="text-gray-500 mb-6">Start adding restaurants to your favourites!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {favouriteRestaurants.map((restaurant) => (
              <div
                key={restaurant.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer relative"
                data-testid={`fav-restaurant-${restaurant.id}`}
              >
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(restaurant.id);
                  }}
                  className="absolute top-2 right-2 z-10 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md"
                  data-testid={`remove-fav-${restaurant.id}`}
                >
                  <Star size={18} className="text-yellow-500 fill-yellow-500" />
                </button>
                <div 
                  onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                  className="relative h-36"
                >
                  <img
                    src={restaurant.image}
                    alt={restaurant.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-white px-2 py-1 rounded-lg flex items-center gap-1 text-sm font-medium">
                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                    <span>{restaurant.rating}</span>
                  </div>
                </div>
                <div className="p-4" onClick={() => navigate(`/restaurant/${restaurant.id}`)}>
                  <h3 className="font-semibold text-gray-900">{restaurant.name}</h3>
                  <p className="text-sm text-gray-500">{restaurant.cuisine}</p>
                  <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{restaurant.delivery_time}</span>
                    </div>
                    <span>•</span>
                    <span>{restaurant.distance} km</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavouriteRestaurantsPage;
